import React from 'react';
import { CheckIcon } from '@guryou/html-components';
import { Box } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { dealTokens } from 'components/shared';

// Shared visual for StaffCube.js and LocationCube.js — both were the same bordered/avatar/label
// card with two different layout mechanics (inline-flow vs absolute-positioned avatar) and
// hardcoded hex colors. One implementation now; only per-item icon and cube width vary by caller,
// since location labels are full phrases (need to wrap) while staff names are short (still wrap,
// just rarely need to).
export const DEFAULT_CUBE_WIDTH = 145;

const useStyles = makeStyles({
  cube: {
    width: '100%',
    minHeight: 140,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: `background ${dealTokens.motion.normal}, border-color ${dealTokens.motion.normal}`,
    borderRadius: dealTokens.radius.md,
    border: `1px solid ${dealTokens.color.border}`,
    backgroundColor: dealTokens.color.canvas,
    padding: dealTokens.space[2],
    textAlign: 'center',
    '&:hover': {
      backgroundColor: dealTokens.color.canvas,
      borderColor: dealTokens.color.primary,
    },
  },
  selected: {
    borderColor: dealTokens.color.success,
    backgroundColor: dealTokens.color.successSoft,
  },
  avatar: {
    width: 44,
    height: 44,
    marginBottom: dealTokens.space[2],
    backgroundColor: dealTokens.color.border,
    color: dealTokens.color.surface,
    fontSize: '1.2rem',
    transition: `background ${dealTokens.motion.normal}`,
  },
  avatarSelected: {
    backgroundColor: dealTokens.color.success,
  },
  nameText: {
    fontSize: '0.8rem',
    fontWeight: dealTokens.font.weightSemibold,
    color: dealTokens.color.text,
    maxWidth: '100%',
    whiteSpace: 'normal',
    lineHeight: 1.3,
    overflowWrap: 'break-word',
  },
});

// `items`: [{ id, label, icon }] — `icon` renders when the item isn't selected; a CheckIcon
// always renders instead once it is. `cubeWidth` sets both the per-cube size and the grid's
// `maxWidth` cap; `minmax(0, 1fr)` (not bare `1fr`) is what actually guarantees equal columns —
// a bare `1fr` track's implicit `auto` minimum lets a long unbroken label widen its own column.
const SelectableCubeGrid = ({ items, selectedIds, onToggle, cubeWidth = DEFAULT_CUBE_WIDTH, gap = dealTokens.space[3] }) => {
  const classes = useStyles();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(items.length, 1)}, minmax(0, 1fr))`,
        gap,
        maxWidth: cubeWidth * items.length + gap * (items.length - 1),
      }}
    >
      {items.map(item => {
        const isSelected = selectedIds.includes(item.id);
        return (
          <Box key={item.id} className={`${classes.cube} ${isSelected ? classes.selected : ''}`} onClick={() => onToggle(item.id)}>
            <Avatar className={`${classes.avatar} ${isSelected ? classes.avatarSelected : ''}`}>{isSelected ? <CheckIcon /> : item.icon}</Avatar>
            <Typography className={classes.nameText}>{item.label}</Typography>
          </Box>
        );
      })}
    </div>
  );
};

export default SelectableCubeGrid;
